import { onMount, Show, createEffect, createSignal } from "solid-js";
import { Portal } from "solid-js/web";
import { IoPlay, IoPause } from "solid-icons/io";
import { FiFastForward, FiClock } from "solid-icons/fi";

import { selectedTime, isPlaying, setIsPlaying, setSelectedTime, playbackSpeed, setPlaybackSpeed, timeWindow, setTimeWindow, initializeIsPlayingFollowEffect, requestTimeControl, smoothPlaybackTimeForTrack, liveMode, livePlaying, setLivePlaying } from "../../store/playbackStore";
import { selectedRange, clearSelection } from "../../store/selectionStore";
import { getCurrentDatasetTimezone } from "../../store/datasetTimezoneStore";
import {
  FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN,
  FLEET_NO_RACE_TIME_WINDOW_CYCLE,
  FLEET_TIME_WINDOW_CYCLE,
  isLongBrushSelection,
  resetFleetPreBrushTimeWindow,
} from "../../utils/fleetTimeWindow";

export interface PlayPauseComponentProps {
  position?: "bottom-left" | "bottom-bottom-left" | "top-right" | "videosync-bottom-left" | "videosync-bottom-center" | "bottom-center" | "map-area-bottom" | "timeline-top" | "timeline-overlay" | "page-bottom-center";
  allowFastFwd?: boolean;
  allowTimeWindow?: boolean;
  /** When true, time-window control never shows or uses "Full" (0); default is 1 min. */
  hideFullTimeWindow?: boolean;
  /** When set, formats the time readout (e.g. Video Sync passes display timezone for local labels). */
  displayTimezone?: string | null;
}

const PlayPauseComponent = (props: PlayPauseComponentProps) => {
  let containerRef: HTMLElement | undefined;
  let timeInputRef: HTMLInputElement | undefined;
  const [showTimePicker, setShowTimePicker] = createSignal(false);
  const [timePickerDate, setTimePickerDate] = createSignal("");
  const [timePickerTime, setTimePickerTime] = createSignal("");
  const [timePickerError, setTimePickerError] = createSignal<string | null>(null);

  /**
   * In live mode this bar IS the live transport, so it has to move both halves of it.
   *
   * `setIsPlaying` alone would stop the clock while leaving `liveFollow` armed — which is what
   * machinery does, not what a user click means — so the live report pages and the live time series
   * would carry on following an edge the map had visibly stopped at, and the button would flip back
   * the moment the live tick auto-resumed near the data edge. Reading `livePlaying()` matters for the
   * same reason: it is the half that only a gesture writes, so it is the only honest thing to display.
   */
  const liveTransportActive = () => liveMode() === true;

  const togglePlay = () => {
    if (liveTransportActive()) {
      setLivePlaying(!livePlaying());
      return;
    }
    setIsPlaying(!isPlaying());
  };

  const toggleSpeed = () => {
    // Speed options: 1x, 2x, 3x, 4x, 5x, 6x (cycles back to 1x)
    // 1x = 1 second per second, 2x = 2 seconds per 1 second, etc.
    const speeds = [1, 2, 3, 4, 5, 6];
    
    const speedValue = typeof playbackSpeed() === "number" ? playbackSpeed() : 1;
    const currentIndex = speeds.indexOf(Number(speedValue) || 1);
    
    // If current speed is not in the available speeds, reset to 1x, otherwise cycle to next speed
    let nextIndex: number;
    if (currentIndex === -1) {
      // Current speed not in list - reset to 1x
      nextIndex = 0;
    } else {
      // Cycle to next speed
      nextIndex = (currentIndex + 1) % speeds.length;
    }
    
    setPlaybackSpeed(speeds[nextIndex]);
  };

  const activeDisplayTimezone = () => props.displayTimezone !== undefined
    ? props.displayTimezone
    : getCurrentDatasetTimezone();

  const getTimeForDisplay = () => isPlaying() ? smoothPlaybackTimeForTrack() : selectedTime();

  const twoDigits = (value: number) => String(value).padStart(2, "0");
  const threeDigits = (value: number) => String(value).padStart(3, "0");

  const getDateParts = (
    date: Date,
    timezone: string | null | undefined
  ): { year: number; month: number; day: number; hour: number; minute: number; second: number; millisecond: number } | null => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    if (!timezone) {
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds(),
        millisecond: date.getMilliseconds(),
      };
    }

    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        hourCycle: "h23",
      });
      const parts = formatter.formatToParts(date);
      const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
      return {
        year: value("year"),
        month: value("month"),
        day: value("day"),
        hour: value("hour"),
        minute: value("minute"),
        second: value("second"),
        millisecond: date.getMilliseconds(),
      };
    } catch {
      return null;
    }
  };

  const formatPickerDate = (date: Date, timezone: string | null | undefined): string => {
    const parts = getDateParts(date, timezone);
    if (!parts) return "";
    return `${parts.year}-${twoDigits(parts.month)}-${twoDigits(parts.day)}`;
  };

  const formatPickerTime = (date: Date, timezone: string | null | undefined): string => {
    const parts = getDateParts(date, timezone);
    if (!parts) return "";
    return `${twoDigits(parts.hour)}:${twoDigits(parts.minute)}:${twoDigits(parts.second)}.${threeDigits(parts.millisecond)}`;
  };

  const parseTimeInput = (value: string): { hour: number; minute: number; second: number; millisecond: number } | null => {
    const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/);
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const second = match[3] === undefined ? 0 : Number(match[3]);
    const millisecond = match[4] === undefined ? 0 : Number(match[4].padEnd(3, "0"));

    if (hour > 23 || minute > 59 || second > 59) return null;
    return { hour, minute, second, millisecond };
  };

  const parseDateInput = (value: string): { year: number; month: number; day: number } | null => {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
      return null;
    }
    return { year, month, day };
  };

  const timezoneWallTimeToDate = (
    dateValue: string,
    timeValue: string,
    timezone: string | null | undefined
  ): Date | null => {
    const dateParts = parseDateInput(dateValue);
    const timeParts = parseTimeInput(timeValue);
    if (!dateParts || !timeParts) return null;

    const { year, month, day } = dateParts;
    const { hour, minute, second, millisecond } = timeParts;

    if (!timezone) {
      const localDate = new Date(year, month - 1, day, hour, minute, second, millisecond);
      return Number.isNaN(localDate.getTime()) ? null : localDate;
    }

    try {
      let candidateMs = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
      for (let i = 0; i < 3; i += 1) {
        const candidate = new Date(candidateMs);
        const parts = getDateParts(candidate, timezone);
        if (!parts) return null;
        const asUtcMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond);
        const targetMs = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
        candidateMs -= asUtcMs - targetMs;
      }

      const result = new Date(candidateMs);
      const resultParts = getDateParts(result, timezone);
      if (
        !resultParts ||
        resultParts.year !== year ||
        resultParts.month !== month ||
        resultParts.day !== day ||
        resultParts.hour !== hour ||
        resultParts.minute !== minute ||
        resultParts.second !== second
      ) {
        return null;
      }
      return result;
    } catch {
      return null;
    }
  };

  const openTimePicker = () => {
    const time = getTimeForDisplay();
    const timezone = activeDisplayTimezone();
    setTimePickerDate(formatPickerDate(time, timezone));
    setTimePickerTime(formatPickerTime(time, timezone));
    setTimePickerError(null);
    setShowTimePicker(true);
    window.setTimeout(() => timeInputRef?.focus(), 0);
  };

  const closeTimePicker = () => {
    setShowTimePicker(false);
    setTimePickerError(null);
  };

  const submitTimePicker = () => {
    const timezone = activeDisplayTimezone();
    const nextTime = timezoneWallTimeToDate(timePickerDate(), timePickerTime(), timezone);
    if (!nextTime) {
      setTimePickerError("Enter a valid date and time.");
      return;
    }

    // Jumping to a chosen time is a user gesture that means "stop following", so in live mode it has
    // to write the transport, not just the clock.
    if (liveTransportActive()) {
      if (livePlaying()) { setLivePlaying(false); }
    } else if (isPlaying()) {
      setIsPlaying(false);
    }

    if (!requestTimeControl("maptimeseries")) {
      setTimePickerError("Unable to take playback time control right now.");
      return;
    }

    setSelectedTime(nextTime, "maptimeseries", true);
    closeTimePicker();
  };

  const toggleTimeWindow = () => {
    const timeWindows = props.hideFullTimeWindow
      ? [...FLEET_NO_RACE_TIME_WINDOW_CYCLE]
      : [...FLEET_TIME_WINDOW_CYCLE];
    const currentWindow = typeof timeWindow() === "number" ? timeWindow() : 0;
    const currentIndex = timeWindows.indexOf(Number(currentWindow) || 0);
    const range = selectedRange && typeof selectedRange === 'function' ? selectedRange() : [];
    const hasBrush = Array.isArray(range) && range.length > 0;
    // If current index not found, start from beginning (0.5 = 30 seconds)
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % timeWindows.length;
    const nextWindowMin = (hasBrush && currentWindow === 0) ? 0.5 : timeWindows[nextIndex];

    // Special case: when a brush exists and current window is Full (0), we jump straight to 30sec
    // and perform updates in an order that avoids a transient "Full" label flash.
    if (hasBrush && currentWindow === 0) {
      try {
        // Switch directly to 30sec timewindow and clear any selection
        setTimeWindow(0.5);
        clearSelection();
        // Clear only brush visuals (defensive) on next tick
        setTimeout(() => {
          try { if ((window as any).clearTimeSeriesBrush) (window as any).clearTimeSeriesBrush(); } catch { /* empty */ }
          try { if ((window as any).clearMapBrush) (window as any).clearMapBrush(); } catch { /* empty */ }
        }, 0);
        return;
      } catch (_) { /* empty */ }
    }

    const valueToSet =
      props.hideFullTimeWindow && currentWindow === 0 && !hasBrush
        ? FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN
        : nextWindowMin;
    if (hasBrush && nextWindowMin > 0) {
      resetFleetPreBrushTimeWindow();
    }
    setTimeWindow(valueToSet);

    try {
      if (hasBrush && nextWindowMin > 0) {
        // Entering timewindow from an existing brush: clear selection and brush UI
        clearSelection();
        try { if ((window as any).clearTimeSeriesBrush) (window as any).clearTimeSeriesBrush(); } catch { /* empty */ }
        try { if ((window as any).clearMapBrush) (window as any).clearMapBrush(); } catch { /* empty */ }
      }

      if (hasBrush && nextWindowMin === 0) {
        clearSelection();
      }
    } catch (_) { /* empty */ }
  };

  function makeDraggable(el: HTMLElement | null) {
    if (!el) return;
    containerRef = el;

    // bottom-center / map-area-bottom / timeline-top / timeline-overlay / page-bottom-center / videosync-bottom-center: positioned by CSS; no drag.
    if (props.position === "bottom-center" || props.position === "map-area-bottom" || props.position === "timeline-top" || props.position === "timeline-overlay" || props.position === "page-bottom-center" || props.position === "videosync-bottom-center") {
      el.style.position = "";
      el.style.left = "";
      el.style.top = "";
      el.style.right = "";
      el.style.bottom = "";
      el.style.transform = "";
      el.style.zIndex = "";
      return;
    }

    // Only keep dynamic positioning logic, static styles are in CSS
    if (el.style.left === "" && el.style.top === "") {
      // Check if we're in split view by looking for split-view-content in the DOM
      // Use a more reliable method that checks multiple indicators
      const isInSplitView = () => {
        // Check for split view container
        const splitViewContent = document.querySelector('.split-view-content');
        if (splitViewContent) return true;
        
        // Check if we're inside a split panel
        const splitPanel = el.closest('.split-panel');
        if (splitPanel) return true;
        
        // Check if the parent container has split view classes
        const parentContainer = el.closest('.map-container');
        if (parentContainer && parentContainer.parentElement?.classList.contains('split-panel')) {
          return true;
        }
        
        return false;
      };
      
      const inSplitView = isInSplitView();
      
      if (inSplitView) {
        // In split view, keep fixed positioning but adjust coordinates
        el.style.position = 'fixed';
        el.style.zIndex = '100000';
      }
      
      if (props.position === "bottom-left") {
        if (inSplitView) {
          // In split view, position relative to viewport
          el.style.left = "100px";
          el.style.top = "calc(100vh - 250px)";
        } else {
          el.style.left = "20%";
          el.style.top = "75%";
        }
      } else if (props.position === "bottom-bottom-left") {
        if (inSplitView) {
          el.style.left = "10px";
          el.style.top = "calc(100vh - 70px)";
        } else {
          el.style.left = "2%";
          el.style.top = "92%";
        }
      } else if (props.position === "top-right") {
        if (inSplitView) {
          el.style.left = "calc(100vw - 400px)";
          el.style.top = "10px";
        } else {
          el.style.left = "75%";
          el.style.top = "10%";
        }
      } else if (props.position === "videosync-bottom-left") {
        // VideoSync specific positioning: 20px from left, 20px from bottom
        el.style.position = 'fixed';
        el.style.left = "20px";
        el.style.bottom = "20px";
        el.style.top = "auto";
        el.style.zIndex = '100000';
      } else {
        if (inSplitView) {
          el.style.left = "calc(100vw - 400px)";
          el.style.top = "calc(100vh - 100px)";
        } else {
          el.style.left = "20%";
          el.style.top = "75%";
        }
      }
    }

    el.addEventListener("mousedown", (event: MouseEvent) => {
      // Only handle drag if clicking on the container itself, not on buttons
      if (
        event.target === el ||
        (event.target as HTMLElement).classList.contains("time-display")
      ) {
        // Convert bottom positioning to top positioning for dragging
        if (el.style.bottom && el.style.top === "auto") {
          const rect = el.getBoundingClientRect();
          el.style.top = `${window.innerHeight - rect.bottom}px`;
          el.style.bottom = "auto";
        }
        
        const offsetX = event.clientX - el.offsetLeft;
        const offsetY = event.clientY - el.offsetTop;

        function onMouseMove(moveEvent: MouseEvent) {
          el.style.left = `${moveEvent.clientX - offsetX}px`;
          el.style.top = `${moveEvent.clientY - offsetY}px`;
          // Ensure bottom is cleared when dragging
          if (el.style.bottom) {
            el.style.bottom = "auto";
          }
        }

        function onMouseUp() {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
        }

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);

        // Prevent text selection during drag
        event.preventDefault();
      }
    });
  }

  // Format the time for display
  const formatTimeDisplay = () => {
    try { 
      const time = getTimeForDisplay();
      const timezone = activeDisplayTimezone();

      const options: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 1,
        hour12: false,
        timeZone: timezone || undefined,
      };

      return new Intl.DateTimeFormat('en-US', options).format(time);
    } catch {
      return "Invalid Time"
    }
  };

  // When Full is hidden (no real race selected), normalize 0 to default unless a long brush is active.
  createEffect(() => {
    if (!props.hideFullTimeWindow || timeWindow() !== 0) return;
    const range = selectedRange && typeof selectedRange === "function" ? selectedRange() : [];
    if (isLongBrushSelection(Array.isArray(range) ? range : [])) return;
    setTimeWindow(FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN);
  });

  // Format the time window for display
  const formatTimeWindowDisplay = () => {
    const range = selectedRange && typeof selectedRange === 'function' ? selectedRange() : [];
    const window = timeWindow();
    if (Array.isArray(range) && range.length > 0) {
      return "Brush";
    }
    if (window === 0) {
      return props.hideFullTimeWindow ? `${FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN}min` : "Full";
    } else if (window === 0.5) {
      return "30sec";
    } else if (window === 1) {
      return "1min";
    } else {
      return `${window}min`;
    }
  };

  // Strict boolean so UI never sees undefined (avoids wrong/missing icon on first load when sync hydrates late on Chrome/macOS)
  const playing = () => (liveTransportActive() ? livePlaying() : isPlaying()) === true;

  onMount(() => {
    // Ensure the component is draggable after mount
    makeDraggable(containerRef);
    // Initialize isPlaying follow effect from a Solid root
    try { initializeIsPlayingFollowEffect(); } catch { /* empty */ }
  });

  return (
    <div
      ref={el => (containerRef = el as HTMLElement)}
      class={`play-pause-container${props.position === "bottom-center" ? " position-bottom-center" : ""}${props.position === "map-area-bottom" ? " map-area-bottom" : ""}${props.position === "timeline-top" ? " position-timeline-top" : ""}${props.position === "timeline-overlay" ? " position-timeline-overlay" : ""}${props.position === "page-bottom-center" ? " position-page-bottom-center" : ""}${props.position === "videosync-bottom-center" ? " position-videosync-bottom-center" : ""}`}
    >
      <button onClick={togglePlay} onContextMenu={(e) => e.preventDefault()} class="play-pause-button" aria-label={playing() ? "Pause" : "Play"}>
        <span class="play-pause-icon-wrap">
          <span classList={{ "play-pause-icon-visible": !playing(), "play-pause-icon-hidden": playing() }} aria-hidden={playing()}>
            <IoPlay />
          </span>
          <span classList={{ "play-pause-icon-visible": playing(), "play-pause-icon-hidden": !playing() }} aria-hidden={!playing()}>
            <IoPause />
          </span>
        </span>
      </button>

      <button
        type="button"
        class="time-display"
        onClick={openTimePicker}
        onMouseDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
        title="Go to time"
        aria-label="Go to selected time"
      >
        {formatTimeDisplay()}
      </button>

      <Show when={props.allowFastFwd}>
        <button onClick={toggleSpeed} onContextMenu={(e) => e.preventDefault()} class="fast-forward-button">
          <FiFastForward />
          <span class="speed-label">
            {playbackSpeed() !== undefined ? `${playbackSpeed()}x` : ""}
          </span>
        </button>
      </Show>

      <Show when={props.allowTimeWindow}>
        <button onClick={toggleTimeWindow} onContextMenu={(e) => e.preventDefault()} class="time-window-button">
          <FiClock />
          <span class="time-window-label">
            {formatTimeWindowDisplay()}
          </span>
        </button>
      </Show>

      <Show when={showTimePicker()}>
        <Portal mount={typeof document !== "undefined" ? (document.getElementById("main-content") || document.body) : undefined}>
          <div class="pagesettings-overlay" onClick={closeTimePicker}>
            <form
              class="pagesettings-modal playback-time-picker-modal"
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => {
                e.preventDefault();
                submitTimePicker();
              }}
            >
              <div class="playback-time-picker-header">
                <h2 class="playback-time-picker-title">Go To Time</h2>
                <button
                  type="button"
                  class="playback-time-picker-close"
                  onClick={closeTimePicker}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div class="playback-time-picker-body">
                <label class="playback-time-picker-field">
                  <span>Date</span>
                  <input
                    type="date"
                    class="playback-time-picker-input"
                    value={timePickerDate()}
                    onInput={(e) => setTimePickerDate(e.currentTarget.value)}
                  />
                </label>

                <label class="playback-time-picker-field">
                  <span>Time</span>
                  <input
                    ref={(el) => (timeInputRef = el)}
                    type="time"
                    step="0.1"
                    class="playback-time-picker-input"
                    value={timePickerTime()}
                    onInput={(e) => setTimePickerTime(e.currentTarget.value)}
                  />
                </label>

                <Show when={activeDisplayTimezone()}>
                  <p class="playback-time-picker-help">Timezone: {activeDisplayTimezone()}</p>
                </Show>

                <Show when={timePickerError()}>
                  <p class="playback-time-picker-error">{timePickerError()}</p>
                </Show>
              </div>

              <div class="playback-time-picker-actions">
                <button type="button" class="playback-time-picker-secondary" onClick={closeTimePicker}>
                  Cancel
                </button>
                <button type="submit" class="playback-time-picker-primary">
                  Go
                </button>
              </div>
            </form>
          </div>
        </Portal>
      </Show>
    </div>
  );
};

export default PlayPauseComponent;

